import { parseHtml, getArticleRoutePrefix } from "@/utils/common";
import Image from "next/image";
import Link from "next/link";
import React from "react";

interface Props {
  data: any;
}

const BlogDetailBanner: React.FC<Props> = ({ data }) => {
  const blog = data?.data?.[0]?.attributes;

  const url = (process.env.NEXT_PUBLIC_REACT_APP_BASE_URL || "").replace(
    /\/$/,
    "",
  );

  const routePrefix = getArticleRoutePrefix(blog);
  const isNewsroom = routePrefix === "newsroom";

  /**
   * Blog URL
   */
  const articleUrl = `${url}/${routePrefix}/${blog?.slug || ""}`;

  /**
   * ChatGPT Prompt
   */
  const prompt = `Provide bullet point summary of the article ${articleUrl}.`;

  /**
   * Encode Prompt
   */
  const encodedPrompt = encodeURIComponent(prompt);

  /**
   * ChatGPT URL
   */
  const chatGptUrl = `https://chatgpt.com/?q=${encodedPrompt}`;

  return (
    <>
      <section className="blog-banner bg-background-dark pt-[121px] md:pt-[155px] xl:pt-[175px] pb-[48px] md:pb-[48px] xl:pb-[48px] relative z-0">
        <div className="container">
          {/* Background Glow */}
          {/* <div className="banner-shape-1 absolute top-[0px] left-0 z-[1] w-[50%] md:w-[40%]">
            <Image
              src="/images/banner-left-layer-mobile.png"
              alt="Banner Shape"
              width={571}
              height={571}
              className="w-full h-full md:hidden"
            />
            <Image
              src="/images/banner-left-layer-tablet.png"
              alt="Banner Shape"
              width={571}
              height={571}
              className="w-full h-full hidden md:block lg:hidden"
            />
            <Image
              src="/images/banner-left-layer-desktop.png"
              alt="Banner Shape"
              width={571}
              height={571}
              className="w-full h-full hidden lg:block"
            />
          </div>
          <div className="banner-shape-1 absolute bottom-0 right-0 z-[-1] ">
            <Image
              src="/images/banner-right-layer-mobile.png"
              alt="Banner Shape"
              width={471}
              height={471}
              className="object-cover w-full h-full md:hidden"
            />
            <Image
              src="/images/banner-right-layer-tablet.png"
              alt="Banner Shape"
              width={471}
              height={471}
              className="object-cover w-full h-full hidden md:block lg:hidden"
            />
            <Image
              src="/images/banner-right-layer-desktop.png"
              alt="Banner Shape"
              width={471}
              height={471}
              className="object-cover w-full h-full hidden lg:block"
            />
          </div> */}

          {/* Breadcrumb */}
          <Link
            href={`/${routePrefix}`}
            className="text-left text-gray-300 font-[400] text-subtitle-4 md:mb-[32px] mb-[16px] block"
          >
            Insights /
            <span>
              {blog?.popluarity === "whitepaper"
                ? " Whitepaper"
                : isNewsroom
                  ? " Newsroom / "
                  : " Blog / "}
            </span>
            {blog?.popluarity !== "whitepaper" && (
              <span>{isNewsroom ? "Newsroom Detail" : "Blog Detail"}</span>
            )}
          </Link>

          {/* Heading */}
          <div className="blog-heading mb-[32px]">
            <h1 className="text-left text-white font-bold text-heading-2 mb-[12px]">
              {blog?.title ?? ""}
            </h1>

            <div className="text-[#C6C7CA] text-left font-regular text-subtitle-3 [&>p]:mb-2">
              {blog?.content ? parseHtml(blog?.content) : null}
            </div>

            {blog?.popluarity !== "whitepaper" && (
              <p className="mt-3 text-[#C6C7CA] text-left font-regular text-subtitle-3">
                Posted by{" "}
                {blog?.authors?.data?.[0]?.attributes?.author_data?.fullname ??
                  ""}{" "}
                |{" "}
                {blog?.publish_at
                  ? new Date(blog?.publish_at).toDateString()
                  : ""}
              </p>
            )}
          </div>

          {/* AI Snapshot Section */}
          <div className="w-full flex">
            {/* Gradient Border Wrapper */}
            <div
              className="w-full"
              style={{
                border: "4px solid",
                borderImageSource:
                  "linear-gradient(135deg, #0086E6 0%, #8ABC00 100%)",
                borderImageSlice: 1,
              }}
            >
              {/* Inner Card */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between bg-gray-700 p-4 md:p-5 lg:p-[12px_24px] gap-4 lg:gap-0">
                {/* Left Section */}
                <div className="flex items-start sm:items-center gap-[16px]">
                  {/* Icon */}
                  <div className="w-[50px] h-[50px] md:w-[60px] md:h-[60px] flex items-center justify-center flex-shrink-0">
                    <Image
                      src="/images/blog/snapshot.png"
                      alt="AI Content Snapshot"
                      width={60}
                      height={60}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  {/* Text */}
                  <div>
                    <h3 className="text-white text-subtitle-2 font-semibold mb-1 md:mb-[4px]">
                      AI Content Snapshot
                    </h3>

                    <p className="text-gray-200 text-subtitle-5">
                      Limited time? Get the essential points in seconds.
                    </p>
                  </div>
                </div>

                {/* Right Section */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 lg:gap-3">
                  <span className="text-white md:text-subtitle-2 text-subtitle-5 font-medium">
                    Get Summary :
                  </span>

                  <div className="flex md:flex-nowrap gap-[10px]">
                    {/* ChatGPT */}
                    <Link
                      href={chatGptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Summarize with ChatGPT"
                      title="Open in ChatGPT"
                      className="p-[10px_12px] text-white text-subtitle-4 border border-gray-300 flex gap-2 items-center md:w-fit w-full justify-center hover:bg-white/10 transition-all duration-300"
                    >
                      <Image
                        src="/images/blog/chatgpt.svg"
                        alt="ChatGPT"
                        width={20}
                        height={20}
                        className="mb-1"
                      />

                      <span>ChatGPT</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* End AI Snapshot Section */}
        </div>
      </section>
    </>
  );
};

export default BlogDetailBanner;
